import * as DG from "ui/dialogs"

import { IconButton, Radio } from "@material-ui/core"
import { default as React, useState } from "react"
import {
  deleteReportingTemplate,
  fetchReportingTemplate,
  fetchReportingTemplates,
} from "api/reporting"
import { queryCache, useMutation, useQuery } from "react-query"

import { AgGridReact } from "ag-grid-react"
import Button from "ui/buttons/Button"
import { CellProps } from "react-table"
import { ReactComponent as DeleteIcon } from "assets/icon-delete.svg"
import { ReportingTemplate } from "models/reporting"
import { Search } from "routes/Base/Admin/components/Search"
import { Table } from "components/tables"
import { css } from "@emotion/css"
import { modalStates } from ".."
import { useI18n } from "providers/I18n"
import { useSnapshot } from "valtio"
import { ViewType } from "./FDReportingGrid/models"

const styles = {
  dialog: css`
    max-width: 551px;
    margin: auto;
  `,
  title: css`
    display: flex;
    flex-direction: column;
  `,
  table: css`
    height: 50vh;
  `,
}

type SearchProps = {
  searchValue: string
  handleSearchChange: (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  handleSearchClear: VoidFunction
}

export const LocalSearch = (searchProps: SearchProps) => {
  const { searchValue, handleSearchChange, handleSearchClear } = searchProps
  const { t } = useI18n()
  return (
    <Search
      name={"name"}
      value={searchValue || ""}
      handleChange={handleSearchChange}
      handleClear={handleSearchClear}
      placeholder={t("inputs.search.placeholder")}
    />
  )
}

export type LoadTemplateModalProps = {
  gridRef: React.RefObject<AgGridReact<any>>
  onClose: () => void
  resetState: () => void
  onLoadFDTemplate?: (
    view: ViewType,
    years: string[],
    scenarios: string[],
  ) => void
}

function LoadTemplateModal(props: LoadTemplateModalProps) {
  const { onClose: _onClose, gridRef, resetState, onLoadFDTemplate } = props
  const { open } = useSnapshot(modalStates.loadTemplate)
  const [selectedID, setSelectedID] = useState<string | undefined>(undefined)
  const [searchTerm, setSearchTerm] = useState<string>("")

  const { t } = useI18n()
  const { data, isFetching: fetchingTemplates } = useQuery(
    "reporting-templates",
    fetchReportingTemplates,
  )

  const filteredData = data?.filter((datum) =>
    datum.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  )
  const onClose = () => {
    setSelectedID(undefined)
    setSearchTerm("")
    _onClose()
  }

  const [deleteTemplate, { isLoading: deletingTemplate }] = useMutation(
    deleteReportingTemplate,
    {
      onSuccess: () => {
        setSelectedID(undefined)
        queryCache.invalidateQueries(["reporting-templates"])
        // onClose()
      },
    },
  )

  const isLoading = fetchingTemplates || deletingTemplate

  const id = "load-reporting-template"

  const handleSubmit = async () => {
    if (!selectedID) return
    const { config: selectedTemplate } =
      await fetchReportingTemplate(selectedID)

    if (!selectedTemplate) {
      return
    }

    resetState()

    onLoadFDTemplate?.(
      selectedTemplate.fdAdditionalConfig?.type!,
      selectedTemplate.fdAdditionalConfig?.selectedYears!,
      selectedTemplate.fdAdditionalConfig?.selectedScenarios!,
    )

    gridRef.current!.api.setFilterModel(selectedTemplate.filterState)
    gridRef.current!.columnApi.applyColumnState({
      state: selectedTemplate.colState,
      applyOrder: true,
    })
    gridRef.current!.columnApi.setPivotMode(selectedTemplate.pivotMode)
    onClose()
  }

  return (
    <DG.Dialog
      className={styles.dialog}
      open={open}
      onClose={onClose}
      id={id}
      disableEscapeKeyDown
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>
          <div className={styles.title}>
            <p className={"HeadlineH5BlackBold"}>
              {t("reporting.template.load.title")}
            </p>
            <p className={"BodyBody-1BlackLight"}>
              {t("reporting.template.load.subtitle")}
            </p>
          </div>
        </DG.DialogTitle>
        <DG.DialogClose onClose={onClose} />
      </DG.DialogHeader>

      <DG.DialogContent paddingTop>
        <Table
          id="reporting-templates-table"
          tableHeight="40vh"
          columns={[
            {
              id: "reporting.first.column",
              fixed: true,
              header: "",
              width: 32,
              Cell: (props: CellProps<ReportingTemplate>) => {
                const { id } = props.row.original
                const isChecked = id === selectedID
                return (
                  <Radio
                    checked={isChecked}
                    onClick={() => {
                      setSelectedID(id)
                    }}
                    color="primary"
                  />
                )
              },
            },
            {
              header: "reporting.templates.column.title",
              id: "name",
              accessor: "name",
            },
            {
              id: "reporting.action.column",
              fixed: true,
              header: "reporting.templates.column.actions",
              width: 59,
              align: "center",
              Cell: (props: CellProps<ReportingTemplate>) => {
                const { id } = props.row.original
                const isChecked = id === selectedID
                return (
                  <IconButton
                    size="small"
                    onClick={() => id && deleteTemplate(id)}
                    disabled={!isChecked}
                  >
                    <DeleteIcon />
                  </IconButton>
                )
              },
            },
          ]}
          data={filteredData || []}
          filters={[]}
          loading={isLoading}
          isPaginated={false}
          localSearch={
            <LocalSearch
              searchValue={searchTerm}
              handleSearchChange={(evt) => {
                setSearchTerm(evt.target.value)
                setSelectedID(undefined)
              }}
              handleSearchClear={() => {
                setSearchTerm("")
                setSelectedID(undefined)
              }}
            />
          }
          hasColumnsReordering={false}
          total={filteredData?.length || 0}
        />
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={onClose}>
            {t("reporting.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={handleSubmit}
            disabled={!selectedID || isLoading}
          >
            {t("reporting.button.use")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export default LoadTemplateModal
